import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Resets the search query the first time it mounts. Used to clear the search
 * when the dropdown menu opens (Headless UI mounts the menu on open, so this
 * fires exactly once per open).
 */
function ResetSearchOnMount({ onReset }: { onReset: () => void }) {
  const onResetRef = useRef(onReset);
  onResetRef.current = onReset;
  useEffect(() => {
    onResetRef.current();
  }, []);
  return null;
}

/**
 * Shared client-side search state for selector dropdowns.
 *
 * Returns the current query, a setter, a case-insensitive substring filter of
 * `items` against the caller-supplied `selector`, and a component that resets
 * the query when it first mounts (intended to be rendered inside DropdownMenu
 * so the query clears each time the menu opens).
 */
export function useDropdownSearch<T>(
  items: T[],
  selector: (item: T) => string,
): {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filteredItems: T[];
  ResetSearchOnMount: () => React.ReactElement | null;
} {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const needle = searchQuery.toLowerCase();
    return items.filter((item) =>
      selector(item).toLowerCase().includes(needle),
    );
  }, [items, searchQuery, selector]);

  // The inner component MUST have a stable identity across parent renders —
  // otherwise React unmounts and remounts it on every keystroke, which
  // re-fires the "reset on mount" effect and clears the search input the
  // moment the user types.
  const BoundResetSearchOnMount = useCallback(
    () => <ResetSearchOnMount onReset={() => setSearchQuery("")} />,
    [],
  );

  return {
    searchQuery,
    setSearchQuery,
    filteredItems,
    ResetSearchOnMount: BoundResetSearchOnMount,
  };
}
